/* eslint-disable no-unused-expressions */
import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Select,
  MenuItem,
  FormControl,
  ListItemText,
  OutlinedInput,
  TextField,
  Autocomplete
} from "@mui/material";

const LOAN_ID_HEADER = "Originator Loan ID / Toorak Loan ID / Originator Name";

const LoanIdListboxComponent = React.forwardRef<HTMLUListElement, any>(
  (props, ref) => {
    const { children, ...rest } = props;
    return (
      <ul ref={ref} {...rest}>
        <li
          style={{
            backgroundColor: "#f5f5f5",
            padding: "8px 12px",
            fontWeight: 600,
            listStyle: "none",
            cursor: "default"
          }}
          role="presentation"
        >
          {LOAN_ID_HEADER}
        </li>
        {children}
      </ul>
    );
  }
);
LoanIdListboxComponent.displayName = "LoanIdListboxComponent";
import {
  DrawStage,
  isRole
  // ExpandCollapse,
} from "@toorak/tc-common-fe-sdk";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useParams } from "react-router-dom";
import { style, useDrawsStyles } from "../Draws.styles";
import { propertFormatter } from "../../../create-loan/loan-lists/DetailsView/ColumnFormatters";

import { isOrigUser } from "../../../utils/AccessManagement";

import { DrawRequestStore } from "../../../stores/drawRequest/drawRequest.interface";
import { RootState } from "../../../stores/rootReducer";
import {
  getDrawDetailsbyLoanId,
  updateDrawDetails
} from "../../../stores/drawRequest/drawRequest.action";
import { updateDrawSelectedProp } from "../../../stores/drawRequest/drawRequest.reducer";
import { useDebounceFn } from "../../../custom-hooks/useDebounce";
import { DrawRolesEnum } from "../../../utils/constants";

export const DrawLoanInformation = ({
  // availableLoanIds,
  // selectedLoanId,
  // handlePropertyChange,
  // selectedProperty,
  // handleLoanIdChange,
  isSubmitClicked,
  showErrorMsg,
  // drawObj,
  // getPropertyView,
  // propertyDetailsOptions,
  isBulkDraw,
  fieldWidth,
  setSnackBarMsg,
  setOpenSnackBar
}: any) => {
  const classes = useDrawsStyles();
  const isOrig = isOrigUser();
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const storeRef = useRef(store);
  storeRef.current = store;
  const [availableLoanIds, setAvailableLoanIds] = useState<any>([]);
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<any[]>([]);
  const [drawObj, setDrawObj] = useState<any>({});
  const { draft } = DrawStage;
  const isDraft = drawObj.stage === draft && !isRole(DrawRolesEnum.ORIGINATOR_VIEW_DRAW);
  const { drawId } = useParams<any>() as any;

  const {
    loanIds,
    drawDetails,
    selectedBankDetails,
    propertyDetailsOptions
  } = useSelector<RootState, DrawRequestStore>(
    (state) => state.drawRequestStore
  );

  // v2 API returns array of { primaryLoanId, toorakLoanId, originatorName }
  useEffect(() => {
    if (loanIds?.length) {
      const options = loanIds.map((item: any, index: number) => {
        if (typeof item === "object" && item?.primaryLoanId != null) {
          return {
            ...item,
            label: `${item.primaryLoanId} / ${item.toorakLoanId ?? ""} / ${item.originatorName ?? ""}`,
            index
          };
        }
        // Legacy: item is a string (primary loan id)
        return {
          primaryLoanId: item,
          toorakLoanId: item,
          originatorName: "",
          label: String(item),
          value: item,
          index
        };
      });
      setAvailableLoanIds(options);
    } else {
      setAvailableLoanIds([]);
    }
    // eslint-disable-next-line
  }, [loanIds]);

  useEffect(() => {
    if (drawDetails) {
      setDrawObj(drawDetails);
      const loanId: string = drawDetails?.primaryLoanId as string;
      setSelectedLoanId(loanId);
      setSelectedProperty(
        drawDetails.propertyDetails?.length
          ? [drawDetails.propertyDetails[0]]
          : []
      );
    }
    // eslint-disable-next-line
  }, [
    drawDetails.primaryLoanId,
    drawDetails.stage,
    drawDetails.propertyDetails
  ]);

  const handleLoanIdChange = (selectedOption: any) => {
    if (!selectedOption) {
      setSelectedLoanId(null);
      return;
    }
    const primaryLoanId =
      typeof selectedOption === "object"
        ? selectedOption.primaryLoanId
        : selectedOption;
    const toorakLoanId =
      typeof selectedOption === "object"
        ? selectedOption.toorakLoanId
        : drawDetails?.toorakLoanId ?? selectedOption;
    setSelectedLoanId(primaryLoanId);
    dispatch(
      getDrawDetailsbyLoanId(
        isOrig,
        toorakLoanId,
        drawId,
        setSnackBarMsg,
        setOpenSnackBar,
        true,
        drawDetails.drawAmountDetails,
        drawObj
      )
    );
  };

  // const removeAllDuplicates = (array: any, key: any) => {
  //   const countMap = array.reduce((acc: any, current: any) => {
  //     const keyValue = current[key];
  //     acc[keyValue] = (acc[keyValue] || 0) + 1;
  //     return acc;
  //   }, {});

  //   return array.filter((item: any) => countMap[item[key]] === 1);
  // };

  const propertyIdMatches = (a: any, b: any) =>
    a != null && b != null && String(a) === String(b);

  const handlePropertyChange = (value: any) => {
    if (value == null || value === "") {
      setSelectedProperty([]);
      dispatch(updateDrawSelectedProp([]));
      return;
    }
    const index = Number(value);
    const option =
      typeof index === "number" &&
      !Number.isNaN(index) &&
      propertyDetailsOptions?.[index] != null
        ? propertyDetailsOptions[index]
        : propertyDetailsOptions?.find((opt: any) =>
            propertyIdMatches(opt?.propertyId, value)
          ) ??
          selectedProperty.find((p: any) =>
            propertyIdMatches(p?.propertyId, value)
          );
    const newSelection = option ? [option] : [];
    setSelectedProperty(newSelection);
    dispatch(updateDrawSelectedProp(newSelection));
  };

  const selectedPropertyValue = (() => {
    const selected = selectedProperty?.[0];
    if (!selected || !propertyDetailsOptions?.length) return "";
    const byRef = propertyDetailsOptions.findIndex((opt: any) => opt === selected);
    if (byRef >= 0) return String(byRef);
    const byId =
      selected?.propertyId != null
        ? propertyDetailsOptions.findIndex((opt: any) =>
            propertyIdMatches(opt?.propertyId, selected?.propertyId)
          )
        : -1;
    if (byId >= 0) return String(byId);
    const byAddress = propertyDetailsOptions.findIndex(
      (opt: any) => opt?.propertyAddress === selected?.propertyAddress
    );
    return byAddress >= 0 ? String(byAddress) : "";
  })();

  // Ref: fingerprint of what we last sent in PUT. Skip scheduling when current state matches (avoids double PUT after response).
  const lastPutFingerprintRef = useRef<string>("");
  // Cooldown after a PUT so we don't schedule again when DrawAmountDetails fires change + blur in quick succession.
  const lastPutAtRef = useRef<number>(0);
  const PUT_COOLDOWN_MS = 1500;

  const debouncedDispatch = useDebounceFn(() => {
    try {
      const state = storeRef.current.getState();
      const currentDraw = state?.drawRequestStore?.drawDetails;
      if (currentDraw?.stage !== draft || isRole(DrawRolesEnum.ORIGINATOR_VIEW_DRAW)) return;
      // Don't PUT when draw was cleared (e.g. after Delete Draw) or no loan selected
      if (!currentDraw?.primaryLoanId) return;

      dispatch(
        updateDrawDetails(
          currentDraw,
          state?.drawRequestStore?.selectedBankDetails,
          drawId,
          isOrig,
          currentDraw.stage,
          currentDraw.originatorId,
          false
        )
      );
      lastPutFingerprintRef.current = JSON.stringify({
        primaryLoanId: currentDraw.primaryLoanId,
        drawAmountDetails: currentDraw.drawAmountDetails,
        propertyDetails: currentDraw.propertyDetails,
        bankDetails: state?.drawRequestStore?.selectedBankDetails
      });
      lastPutAtRef.current = Date.now();
    } catch (e) {
      console.error("Error in dispatching updateDrawDetails:", e);
    }
  }, 500); // Adjust debounce delay as needed

  // Fingerprint: property, loan id, amount details, and bank details so we autosave on any of those changes.
  const drawAmountDetailsKey = JSON.stringify(drawDetails?.drawAmountDetails ?? {});
  const currentFingerprint = JSON.stringify({
    primaryLoanId: drawDetails?.primaryLoanId,
    drawAmountDetails: drawDetails?.drawAmountDetails,
    propertyDetails: drawDetails?.propertyDetails,
    bankDetails: selectedBankDetails
  });

  useEffect(() => {
    // Only allow PUT when current draw is draft (use drawDetails from Redux, not drawObj).
    if (drawDetails?.stage !== draft) return;
    if (isRole(DrawRolesEnum.ORIGINATOR_VIEW_DRAW)) return;
    // Prime ref on first run so we don't PUT on initial load; then skip when state matches last sent.
    if (lastPutFingerprintRef.current === "") {
      lastPutFingerprintRef.current = currentFingerprint;
      return;
    }
    if (currentFingerprint === lastPutFingerprintRef.current) return;
    // Skip if we just sent a PUT (avoids second PUT when DrawAmountDetails does change + blur in quick succession).
    if (Date.now() - lastPutAtRef.current < PUT_COOLDOWN_MS) return;

    // Schedule save when property, loan id, or amount details change
    const cleanup = debouncedDispatch();

    return cleanup;
  }, [
    selectedProperty,
    drawDetails?.primaryLoanId,
    drawAmountDetailsKey,
    drawDetails?.stage,
    draft,
    debouncedDispatch,
    currentFingerprint
  ]);

  const getPropertyView = () => {
    const propertyAddresses =
      selectedProperty.length && typeof selectedProperty[0] !== "string"
        ? selectedProperty.map((items: any) => items?.propertyAddress)
        : selectedProperty;
    const AddressString = {
      row: { properties: propertyAddresses }
    };
    return propertFormatter(
      { row: AddressString.row, key: "propertyDetails" },
      classes,
      true
    );
  };
  /* eslint-disable react/jsx-props-no-spreading */
  return (
    <Grid
      item
      container
      xs={12}
      data-testid="draw-view-itemContainer"
      className={classes.itemContainer}
    >
      <Grid
        item
        container
        xs={10}
        className={classes.itemHeaderText}
        data-testid="draw-view-itemHeaderText"
      >
        Loan Information
      </Grid>

      {/* <Grid
              item
              container
              xs={2}
              data-testid="draw-view-expandCollapse"
              className={classes.expandCollapse}
            >
              <ExpandCollapse
                isMinimize={isMinimizedLoanDetail}
                clicked={() => {
                  setIsMinimizedLoanDetail(!isMinimizedLoanDetail);
                }}
                keyDown={() => {
                  setIsMinimizedLoanDetail(!isMinimizedLoanDetail);
                }}
              />
            </Grid> */}
      <Grid
        item
        container
        xs={12}
        data-testid="draw-view-fieldContainer"
        className={classes.fieldContainer}
      >
        <Grid
          item
          container
          xs={fieldWidth}
          data-testid="draw-view-primary-loan"
          className={classes.fieldRow}
        >
          {isDraft && !isBulkDraw ? (
            <Grid>
              <p className={classes.headerLoanID} data-testid="primary-loan-id">
                Primary Loan ID
                <span className={classes.asterick}>*</span>
              </p>

              <Autocomplete
                id="checkboxes-loanid-demo"
                data-testid="auto-complete-loanid-input"
                options={availableLoanIds}
                getOptionLabel={(option: any) =>
                  option?.primaryLoanId != null ? String(option.primaryLoanId) : ""
                }
                filterOptions={(options: any[], { inputValue }) => {
                  if (!inputValue?.trim()) return options;
                  const search = inputValue.trim().toLowerCase();
                  return options.filter((opt: any) => {
                    const primary = opt?.primaryLoanId != null ? String(opt.primaryLoanId).toLowerCase() : "";
                    const toorak = opt?.toorakLoanId != null ? String(opt.toorakLoanId).toLowerCase() : "";
                    const name = opt?.originatorName != null ? String(opt.originatorName).toLowerCase() : "";
                    return primary.includes(search) || toorak.includes(search) || name.includes(search);
                  });
                }}
                onChange={(event: any, newValue: any) => {
                  handleLoanIdChange(newValue);
                }}
                value={
                  availableLoanIds.find(
                    (opt: any) =>
                      opt?.primaryLoanId === selectedLoanId ||
                      opt?.toorakLoanId === drawDetails?.toorakLoanId
                  ) ?? null
                }
                isOptionEqualToValue={(option: any, value: any) =>
                  option?.primaryLoanId === value?.primaryLoanId ||
                  option?.primaryLoanId === value
                }
                ListboxComponent={LoanIdListboxComponent}
                slotProps={{
                  paper: { style: { minWidth: 420 } }
                }}
                renderOption={(props, option: any) => (
                  <li {...props} key={option?.index ?? option?.primaryLoanId}>
                    {option?.label ??
                      `${option?.primaryLoanId ?? ""} / ${option?.toorakLoanId ?? ""} / ${option?.originatorName ?? ""}`.trim()}
                  </li>
                )}
                style={style}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select"
                  />
                )}
              />
              {isSubmitClicked ? showErrorMsg("selectedLoanId") : null}
            </Grid>
          ) : (
            <div className={classes.itemBox}>
              <p
                className={classes.itemSubHeaderText}
                data-testid="draw-primary-loan-id"
              >
                Primary Loan ID
              </p>
              {drawObj?.primaryLoanId ? (
                <p
                  className={classes.itemValueText}
                  data-testid="primary-loan-value"
                >
                  {drawObj.primaryLoanId}
                </p>
              ) : (
                <p
                  className={classes.itemValueText}
                  data-testid="primary-loan-blank"
                >
                  __ __
                </p>
              )}
            </div>
          )}
        </Grid>
        <Grid
          item
          container
          xs={fieldWidth}
          data-testid="draw-property-details"
          className={classes.fieldRow}
        >
          {isDraft ? (
            <Grid>
              <p
                className={`${classes.headerLoanID} ${
                  selectedLoanId === ""
                    ? classes.disabledHeaderText
                    : classes.headerLoanID
                }`}
              >
                Property Details
                <span
                  className={`${classes.asterick} ${
                    selectedLoanId === ""
                      ? classes.hiddenAsterick
                      : classes.asterick
                  }`}
                >
                  *
                </span>
              </p>
              <div>
                <FormControl
                  variant="outlined"
                  className={classes.formControlOption}
                >
                  <Select
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left"
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left"
                      }
                    }}
                    value={selectedPropertyValue}
                    displayEmpty
                    disabled={selectedLoanId === ""}
                    labelId="select-property"
                    id="demo-simple-select-outlined"
                    placeholder="Select"
                    data-testid="draw-property-dropdown"
                    className={classes.selectRoot}
                    onChange={(e) => handlePropertyChange(e.target.value)}
                    input={<OutlinedInput label="Tag" />}
                    renderValue={(value: any) => {
                      if (value == null || value === "")
                        return <span style={{ color: "#9e9e9e" }}>Select</span>;
                      const idx = Number(value);
                      const obj =
                        !Number.isNaN(idx) && propertyDetailsOptions?.[idx] != null
                          ? propertyDetailsOptions[idx]
                          : selectedProperty?.[0];
                      return obj?.propertyAddress ?? "";
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {propertyDetailsOptions?.length > 0 &&
                      propertyDetailsOptions.map((item: any, index: number) => (
                        <MenuItem
                          key={item?.propertyId ?? index}
                          value={String(index)}
                        >
                          <ListItemText primary={item.propertyAddress} />
                        </MenuItem>
                      ))}
                  </Select>
                  {isSubmitClicked ? showErrorMsg("selectedProperty") : null}
                </FormControl>
              </div>
            </Grid>
          ) : (
            <div className={classes.itemBox}>
              <p
                className={classes.itemSubHeaderText}
                data-testid="draw-property-value"
              >
                Property Details
              </p>
              {selectedProperty.length ? (
                <p
                  className={classes.itemValueText}
                  data-testid="draw-property-options"
                >
                  {getPropertyView()}
                </p>
              ) : (
                <p
                  className={classes.itemValueText}
                  data-testid="draw-property-blank"
                >
                  __ __
                </p>
              )}
            </div>
          )}
        </Grid>
        <Grid
          item
          container
          xs={fieldWidth}
          className={classes.fieldRow}
          data-testid="draw-toorak-loanId"
        >
          <div className={classes.itemBox}>
            <p className={classes.itemSubHeaderText}>Toorak Loan ID</p>
            {drawObj?.toorakLoanId ? (
              <p
                className={classes.itemValueText}
                data-testid="toorak-loanId-value"
              >
                {drawObj.toorakLoanId}
              </p>
            ) : (
              <p
                className={classes.itemValueText}
                data-testid="toorak-loanId-blank"
              >
                __ __
              </p>
            )}
          </div>
        </Grid>
        <Grid item container xs={fieldWidth} className={classes.fieldRow}>
          <div className={classes.itemBox}>
            <p className={classes.itemSubHeaderText}>Originator Name</p>
            {drawObj?.originatorName ? (
              <p
                className={classes.itemValueText}
                data-testid="toorak-originatorName-value"
              >
                {drawObj.originatorName}
              </p>
            ) : (
              <p
                className={classes.itemValueText}
                data-testid="toorak-originatorName-blank"
              >
                __ __
              </p>
            )}
          </div>
        </Grid>
        {!isOrig && (
          <>
            <Grid item container xs={fieldWidth} className={classes.fieldRow}>
              <div className={classes.itemBox}>
                <p className={classes.itemSubHeaderText}>Funding Channel</p>
                {drawObj?.fundingChannel ? (
                  <p
                    className={classes.itemValueText}
                    data-testid="toorak-fundingChannel-value"
                  >
                    {drawObj.fundingChannel}
                  </p>
                ) : (
                  <p
                    className={classes.itemValueText}
                    data-testid="toorak-fundingChannel-blank"
                  >
                    __ __
                  </p>
                )}
              </div>
            </Grid>
            <Grid item container xs={fieldWidth} className={classes.fieldRow}>
              <div className={classes.itemBox}>
                <p className={classes.itemSubHeaderText}>Seller Name</p>
                {drawObj?.sellerName ? (
                  <p
                    className={classes.itemValueText}
                    data-testid="toorak-sellerName-value"
                  >
                    {drawObj.sellerName}
                  </p>
                ) : (
                  <p
                    className={classes.itemValueText}
                    data-testid="toorak-sellerName-blank"
                  >
                    __ __
                  </p>
                )}
              </div>
            </Grid>
            <Grid item container xs={fieldWidth} className={classes.fieldRow}>
              <div className={classes.itemBox}>
                <p className={classes.itemSubHeaderText}>Broker Name</p>
                {drawObj?.brokerName ? (
                  <p
                    className={classes.itemValueText}
                    data-testid="toorak-brokerName-value"
                  >
                    {drawObj.brokerName}
                  </p>
                ) : (
                  <p
                    className={classes.itemValueText}
                    data-testid="toorak-brokerName-blank"
                  >
                    __ __
                  </p>
                )}
              </div>
            </Grid>
            <Grid item container xs={fieldWidth} className={classes.fieldRow}>
              <div className={classes.itemBox}>
                <p className={classes.itemSubHeaderText}>Originating Entity</p>
                {drawObj?.originatingEntity ? (
                  <p
                    className={classes.itemValueText}
                    data-testid="toorak-originatingEntity-value"
                  >
                    {drawObj.originatingEntity}
                  </p>
                ) : (
                  <p
                    className={classes.itemValueText}
                    data-testid="toorak-originatingEntity-blank"
                  >
                    __ __
                  </p>
                )}
              </div>
            </Grid>
          </>
        )}
        <Grid item container xs={fieldWidth} className={classes.fieldRow}>
          <div className={classes.itemBox}>
            <p className={classes.itemSubHeaderText}>Servicer ID</p>
            {drawObj?.servicerId ? (
              <p
                className={classes.itemValueText}
                data-testid="toorak-servicerId-value"
              >
                {drawObj.servicerId}
              </p>
            ) : (
              <p
                className={classes.itemValueText}
                data-testid="toorak-servicerId-blank"
              >
                __ __
              </p>
            )}
          </div>
        </Grid>
        <Grid item container xs={fieldWidth} className={classes.fieldRow}>
          <div className={classes.itemBox}>
            <p className={classes.itemSubHeaderText}>Servicer Name</p>
            {drawObj?.servicerName ? (
              <p
                className={classes.itemValueText}
                data-testid="toorak-servicerName-value"
              >
                {drawObj.servicerName}
              </p>
            ) : (
              <p
                className={classes.itemValueText}
                data-testid="toorak-servicerName-blank"
              >
                __ __
              </p>
            )}
          </div>
        </Grid>
        <Grid item container xs={fieldWidth} className={classes.fieldRow}>
          <div className={classes.itemBox}>
            <p className={classes.itemSubHeaderText}>Loan Type</p>
            {drawObj?.loanType ? (
              <p
                className={classes.itemValueText}
                data-testid="toorak-loanType-value"
              >
                {drawObj.loanType}
              </p>
            ) : (
              <p
                className={classes.itemValueText}
                data-testid="toorak-loanType-blank"
              >
                __ __
              </p>
            )}
          </div>
        </Grid>
        <Grid item container xs={fieldWidth} className={classes.fieldRow}>
          <div className={classes.itemBox}>
            <p className={classes.itemSubHeaderText}>Draw Number</p>
            {drawObj?.drawNumber ? (
              <p
                className={classes.itemValueText}
                data-testid="toorak-drawNumber-value"
              >
                {drawObj.drawNumber}
              </p>
            ) : (
              <p
                className={classes.itemValueText}
                data-testid="toorak-drawNumber-blank"
              >
                __ __
              </p>
            )}
          </div>
        </Grid>
        <Grid item container xs={fieldWidth} className={classes.fieldRow}>
          <div className={classes.itemBox}>
            <p className={classes.itemSubHeaderText}>Borrower Name</p>
            {drawObj?.borrowerEntityName ? (
              <p
                className={classes.itemValueText}
                data-testid="toorak-borrowerEntityName-value"
              >
                {drawObj.borrowerEntityName}
              </p>
            ) : (
              <p
                className={classes.itemValueText}
                data-testid="toorak-borrowerEntityName-blank"
              >
                __ __
              </p>
            )}
          </div>
        </Grid>
        <Grid item container xs={fieldWidth} className={classes.fieldRow}>
          <div className={classes.itemBox}>
            <p className={classes.itemSubHeaderText}>Address</p>
            {drawObj?.borrowerAddress ? (
              <p
                className={classes.itemValueText}
                data-testid="toorak-borrowerAddress-value"
              >
                {drawObj.borrowerAddress}
              </p>
            ) : (
              <p
                className={classes.itemValueText}
                data-testid="toorak-borrowerAddress-blank"
              >
                __ __
              </p>
            )}
          </div>
        </Grid>
        {/* <DrawBalanceToFinish loanId={selectedLoanId} /> */}
      </Grid>
    </Grid>
  );
};